import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { GuideUserStatus } from "@mapmycustomers/shared/types/entity";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";

import GuideStartMode from "@app/types/GuideStartMode";
import ShowGuideOptions from "@app/types/ShowGuideOptions";

import { ANNOUNCEMENTS_GUIDE_ID, IGuidesService } from "./GuidesContext";

type GuideVisibility = Record<string, boolean>;

const isAnotherGuideHeld = (visibility: GuideVisibility, slug: string) =>
  Object.entries(visibility).some(([id, visible]) => visible && id !== slug);

const isAnnouncementsSlotHeld = (visibility: GuideVisibility) =>
  !!visibility[ANNOUNCEMENTS_GUIDE_ID];

const isStartRefusedByMode: Record<
  GuideStartMode,
  (visibility: GuideVisibility, slug: string) => boolean
> = {
  auto: isAnotherGuideHeld,
  force: () => false,
  requested: isAnnouncementsSlotHeld,
  restart: isAnnouncementsSlotHeld,
};

const useGuidesService = (
  myGuideStatuses: GuideUserStatus[],
  updateGuideStatus: IGuidesService["updateGuideStatus"],
  signedIn?: boolean
): IGuidesService => {
  const [visibility, setVisibility] = useState<GuideVisibility>({});
  // Written at call time, so a start later in the same tick sees a slot taken before it renders.
  const heldRef = useRef<GuideVisibility>({});
  const completionPaths = useRef<Record<string, string>>({});

  // A guide open at sign-out must not carry over to the next user's session.
  useEffect(() => {
    if (signedIn === false) {
      heldRef.current = {};
      completionPaths.current = {};
      setVisibility({});
    }
  }, [signedIn]);

  const setGuideVisible = useCallback((slug: string, visible: boolean) => {
    heldRef.current = { ...heldRef.current, [slug]: visible };
    setVisibility((current) => ({ ...current, [slug]: visible }));
  }, []);

  const startGuide = useCallback(
    (slug: string, completionPath?: null | string) => {
      if (!completionPath) {
        if (completionPath === null) {
          delete completionPaths.current[slug];
        }
      } else {
        completionPaths.current[slug] = completionPath;
      }
      setGuideVisible(slug, true);
    },
    [setGuideVisible]
  );

  const hideGuide = useCallback((slug: string) => setGuideVisible(slug, false), [setGuideVisible]);

  const showGuide = useCallback(
    (slug: string, { completionPath, mode }: ShowGuideOptions) => {
      if (isStartRefusedByMode[mode](heldRef.current, slug)) {
        return false;
      }
      if (mode === "restart") {
        // Hides it, then shows it on the next tick; its slot stays held in between.
        heldRef.current = { ...heldRef.current, [slug]: true };
        setVisibility((current) => ({ ...current, [slug]: false }));
        window.setTimeout(() => startGuide(slug, completionPath));
      } else {
        startGuide(slug, completionPath);
      }
      return true;
    },
    [startGuide]
  );

  const getGuideStatus = useDynamicCallback((slug: string) =>
    myGuideStatuses.find(({ guide }) => guide.frigadeFlowId === slug)
  );

  return useMemo(
    () => ({
      getCompletionPath: (slug: string) => completionPaths.current[slug],
      getGuideStatus,
      hideGuide,
      showGuide,
      updateGuideStatus,
      visibility,
    }),
    [getGuideStatus, hideGuide, showGuide, updateGuideStatus, visibility]
  );
};

export default useGuidesService;
